/**
 * Proof of Grind – schakelaar "Met lijst / Alleen poster".
 *
 * - Leest de gekozen optiewaarden uit Horizon's <variant-picker> (nooit hardgecodeerde namen).
 * - Bij wisselen: haalt {ander product}.js op, zoekt de variant met hetzelfde Formaat
 *   (en voor 'met lijst' de laatst gekozen lijst uit sessionStorage), laadt die pagina via
 *   Section Rendering (morph van <main>) en werkt de URL bij. Lukt dat niet: gewone navigatie.
 * - Standaardvariant (30×40 / eerste lijst) wordt server-side gekozen (snippets/pog-variant-index.liquid).
 */
import { navigateMorph } from '@pog/navigate';

const STORAGE_KEY = 'pog-lijst';

function store(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    /* privé-modus of geblokkeerd */
  }
}

function read(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function pickerIn(root) {
  return root.querySelector('variant-picker[data-template-product-match="true"]') || root.querySelector('variant-picker');
}

/** Gekozen opties als {naam: waarde}, uit radio's of selects. */
function selectedOptions(picker) {
  const out = {};
  if (!picker) return out;
  picker.querySelectorAll('input[type="radio"]:checked').forEach((i) => {
    if (i.dataset.optionName) out[i.dataset.optionName] = i.value;
  });
  picker.querySelectorAll('select').forEach((s) => {
    const o = s.selectedOptions[0];
    if (o?.dataset.optionName) out[o.dataset.optionName] = o.value;
  });
  return out;
}

class PogLijstSchakelaar extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('.pog-switch__button'));
    this.buttons.forEach((b) => b.addEventListener('click', () => this.switchTo(b.dataset.target)));

    // Na een morph van <main> blijft dit element bestaan; lees daarom alles live.
    const section = this.closest('.shopify-section') || document;
    section.addEventListener('change', (e) => {
      if (e.target instanceof HTMLElement && e.target.closest('variant-picker')) this.rememberLijst();
    });
    this.rememberLijst();
  }

  get picker() {
    return pickerIn(this.closest('.shopify-section') || document);
  }

  get formaatName() {
    return this.dataset.formaatOption || '';
  }

  rememberLijst() {
    if (this.dataset.type !== 'ingelijst') return;
    const opts = selectedOptions(this.picker);
    const names = Object.keys(opts).filter((n) => n !== this.formaatName);
    if (names.length) store(STORAGE_KEY, opts[names[0]]);
  }

  async switchTo(target) {
    if (target === this.dataset.type) return;
    const targetUrl = target === 'poster' ? this.dataset.posterUrl : this.dataset.framedUrl;
    if (!targetUrl) return;

    const current = selectedOptions(this.picker);
    const formaat = current[this.formaatName];
    const lijst = read(STORAGE_KEY);

    this.buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));

    let variantId = null;
    try {
      const res = await fetch(`${targetUrl.split('?')[0]}.js`, { headers: { Accept: 'application/json' } });
      const product = await res.json();
      const optionNames = (product.options || []).map((o) => (typeof o === 'string' ? o : o.name));
      const fIdx = optionNames.indexOf(this.formaatName);
      const candidates = (product.variants || []).filter((v) => fIdx === -1 || v.options[fIdx] === formaat);
      let match = null;
      if (target === 'ingelijst' && lijst) {
        match = candidates.find((v) => v.options.some((val, i) => i !== fIdx && val === lijst));
      }
      match = match || candidates.find((v) => v.available) || candidates[0] || product.variants?.[0];
      variantId = match ? match.id : null;
    } catch (e) {
      variantId = null;
    }

    const dest = new URL(targetUrl, window.location.origin);
    if (variantId) dest.searchParams.set('variant', String(variantId));

    await navigateMorph(dest.toString());
  }
}

if (!customElements.get('pog-lijst-schakelaar')) {
  customElements.define('pog-lijst-schakelaar', PogLijstSchakelaar);
}
